<template>
  <div>
    <spinner v-if="loading" />
    <div v-else class="design-webshop-ticket-container">
      <div class="design-webshop-ticket-main-container">
        <h1>{{ adminText('workflowsAll.design_webshop_tickets_8a7e7d9', 'Design webshop tickets') }}</h1>
        <p class="description">
          {{ adminText('workflowsAll.customize_the_visual_elements_your_customers__4931b42', 'Customize the visual elements your customers will see when buying tickets online.') }}
        </p>
        <h2 style="margin-top: 2.12rem">{{ adminText('workflowsAll.1_main_image_0f84133', '1 - Main image') }}</h2>
        <div class="reuse-cover-image">
          <p>{{ adminText('workflowsAll.reuse_event_cover_image_0f44157', 'Reuse event cover image') }}</p>
          <div>
            <v-switch
              v-model="useEventCover"
              hide-details
              :disabled="useEventCover || !eventData.cover"
              @change="handleUseEventCoverChange"
            />
          </div>
        </div>
        <cover-field
          v-model="form.mainImage"
          style="margin-top: 0.75rem"
          label-title="ADD MAIN IMAGE"
          disable-delete
          @input="handleImageChange"
        />
        <h2 style="margin-top: 0.875rem">{{ adminText('workflowsAll.2_organizer_message_94ca6d1', '2 - Organizer message') }}</h2>
        <textarea-field
          v-model="form.organizerMessage"
          style="margin-top: 0.75rem"
          :label="adminText('workflowsAll.organizer_message_851e730', 'Organizer message')"
          :validator="$v.form.organizerMessage"
          :error-messages="{ maxLength: adminText('workflowsAll.organizer_message_is_limited_to_500_character_e58d15d', 'Organizer message is limited to 500 characters.') }"
          @input="handleOrganizerMessageChange"
        />
        <h2 style="margin-top: 1.75rem">{{ adminText('workflowsAll.3_additional_visual_optional_df550bc', '3 – Additional visual (optional)') }}</h2>
        <p class="description" style="margin-top: 0.75rem">
          {{ adminText('workflowsAll.we_recommend_placing_the_logos_of_your_event__51f2470', 'We recommend placing the logos of your event\'s partners/sponsors here or adding an extra branding touch to the event.') }}
        </p>
        <cover-field-horizontal
          v-model="form.additionalVisual"
          style="margin-top: 1.5rem"
          @input="handleImageChange"
        />
      </div>
      <div class="aside-container">
        <div class="preview-title-container">
          <h2>{{ adminText('workflowsAll.preview_f1fbb2b', 'Preview') }}</h2>
          <v-btn fab flat style="background: white" :href="pdfUrl" target="_blank">
            <v-icon color="primary">open_in_new</v-icon>
          </v-btn>
        </div>
        <spinner v-if="saving" />
        <div v-else style="margin-top: 1rem">
          <vue-pdf-embed v-if="pdfUrl" :source="pdfUrl" class="pdf-iframe" />
        </div>
      </div>
    </div>
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="workflowLabel(snackbar.message)"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import CoverFieldHorizontal from '@/components/shared/inputs/cover-field-horizontal.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import {
  GET_EVENT_DYNAMIC_WEBSHOP_TICKET_DATA,
  UPDATE_EVENT_DYNAMIC_WEBSHOP_TICKET_DATA,
} from '@/graphql/event.graphql'
import {
  GET_EVENT_TEMPLATE_DYNAMIC_WEBSHOP_TICKET_DATA,
  UPDATE_EVENT_TEMPLATE_DYNAMIC_WEBSHOP_TICKET_DATA,
} from '@/graphql/eventTemplate.graphql'
import { maxLength } from 'vuelidate/lib/validators'
import { debounce } from 'lodash'
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'

export default {
  mixins: [WorkflowDisplay],
  name: 'design-webshop-ticket-container',
  components: {
    CoverFieldHorizontal,
    SimpleSnackbar,
    VuePdfEmbed,
  },
  props: {
    organizationSlug: String,
    eventId: String,
    eventTemplateId: String,
  },
  data() {
    return {
      eventData: null,
      form: null,
      loading: true,
      saving: false,
      snackbar: {
        open: false,
        message: '',
        color: 'error',
      },
    }
  },
  validations() {
    return {
      form: {
        organizerMessage: {
          maxLength: maxLength(500),
        },
      },
    }
  },
  computed: {
    useEventCover() {
      return this.eventData.cover && this.form.mainImage === this.eventData.cover
    },
    pdfUrl() {
      if (!this.eventData || !this.eventData.id) {
        return null
      }

      return `${process.env.VUE_APP_SERVER_URL}/files/online-ticket-preview/${this.eventData.id}`
    },
  },
  created() {
    this.debouncedSaveChanges = debounce(this.saveChanges, 800)
  },
  async mounted() {
    await this.fetchData()
  },
  methods: {
    async fetchData() {
      if (this.eventTemplateId) {
        const { data } = await this.$apollo.query({
          query: GET_EVENT_TEMPLATE_DYNAMIC_WEBSHOP_TICKET_DATA,
          variables: {
            organizationSlug: this.organizationSlug,
            eventTemplateId: this.eventTemplateId,
          },
        })

        if (data.getEventsByEventTemplateId && data.getEventsByEventTemplateId.length > 0) {
          this.eventData = data.getEventsByEventTemplateId[0]
        }
      } else {
        const { data } = await this.$apollo.query({
          query: GET_EVENT_DYNAMIC_WEBSHOP_TICKET_DATA,
          variables: { id: this.eventId },
        })
        this.eventData = data.getEvent
      }

      this.form = {
        mainImage:
          (this.eventData.metadata && this.eventData.metadata.mainImage) || this.eventData.cover,
        organizerMessage: this.eventData.organizerMessage,
        additionalVisual: this.eventData.metadata && this.eventData.metadata.additionalVisual,
      }
      this.loading = false
    },
    // Handle image changes - save immediately
    async handleImageChange() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }
      await this.saveChanges()
    },

    // Handle organizer message changes - debounced save
    handleOrganizerMessageChange() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }
      this.debouncedSaveChanges()
    },

    async handleUseEventCoverChange() {
      this.form.mainImage = this.eventData.cover
      await this.handleImageChange()
    },

    async saveChanges() {
      if (this.saving) return

      this.saving = true

      if (this.eventTemplateId) {
        await this.saveEventTemplateChanges()
      } else {
        await this.saveEventChanges()
      }

      this.saving = false
    },

    async saveEventTemplateChanges() {
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_EVENT_TEMPLATE_DYNAMIC_WEBSHOP_TICKET_DATA,
        variables: {
          eventTemplateId: this.eventTemplateId,
          mainImage: this.form.mainImage === this.eventData.cover ? null : this.form.mainImage,
          organizerMessage: this.form.organizerMessage,
          additionalVisual: this.form.additionalVisual,
        },
      })

      if (errors) {
        this.snackbar.open = true
        this.snackbar.message = 'workflowsAll.error_in_updating_data_c9e96a4'
      }
    },

    async saveEventChanges() {
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_EVENT_DYNAMIC_WEBSHOP_TICKET_DATA,
        variables: {
          eventId: this.eventId,
          mainImage: this.form.mainImage,
          organizerMessage: this.form.organizerMessage,
          additionalVisual: this.form.additionalVisual,
        },
      })

      if (errors) {
        this.snackbar.open = true
        this.snackbar.message = 'workflowsAll.error_in_updating_data_c9e96a4'
      }
    },
  },
}
</script>

<style scoped>
.design-webshop-ticket-container {
  display: flex;
  gap: 1.5rem;
}

.design-webshop-ticket-main-container {
  max-width: 35.375rem;
  flex-shrink: 1;
}

.aside-container {
  flex: 0 0 20.625rem;
  max-width: 100%;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 1rem;
  height: fit-content;
}

h1 {
  color: #ff4c00;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.045rem;
}

@media screen and (max-width: 800px) {
  h1 {
    display: none;
  }
}

.description {
  color: #979797;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin: 1rem 0 0 0;
}

h2 {
  color: #0277bd;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
}

.reuse-cover-image {
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.reuse-cover-image p {
  color: #333;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  margin: 0;
}

::v-deep .reuse-cover-image .v-input--switch {
  padding: 0;
  margin: 0;
}

@media screen and (max-width: 1100px) {
  .design-webshop-ticket-container {
    flex-direction: column;
  }

  .aside-container {
    flex: 1;
    width: 100%;
  }
}

.pdf-iframe {
  border: 0.90303px solid #d9d9d9;
  border-radius: 7.22px;
  width: 100%;
  overflow: hidden;
}

.preview-title-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
